import React, { Component } from 'react';
import { computed, autorun, observable } from 'mobx';
import { observer, inject, disposeOnUnmount } from 'mobx-react';
import _ from 'lodash';
import { withRouter } from 'react-router-dom';
import { withOrdersStore } from 'src/stores/orders';
import OrderNotification from 'src/dispensary-admin/orders/modals/order-notification';
import Push from 'push.js';

@inject('UI', 'User')
@withOrdersStore
@observer
class OpenOrdersNotification extends Component {
  lastDesktopNotificationId;

  // Alert on an order's first appearance, whatever status it arrives in.
  // Auto-confirmed orders are flipped to confirmed inside AfterCreate before
  // PublishOrderChange fires, and the Pusher handler refetches instead of using
  // the payload -- so they are never seen as `open`. Tracking arrival also keeps
  // a manual confirm silent, since that order's id is already recorded.
  // Not observable: autoNotify is the sole reader/writer. (ECOMM-11139)
  seenOrderIds = new Set();

  seededInitialOrders = false;

  @computed get shouldRespondToNewOrder() {
    const { User } = this.props;
    const { permissions } = User.profile;

    if (User.isSuperAdmin || !permissions?.orders) {
      return false;
    }

    return true;
  }

  @disposeOnUnmount autoNotify = autorun(() => {
    const { ordersStore } = this.props;
    const { initialLoadFinished, orders } = ordersStore.active;

    if (!initialLoadFinished) {
      return;
    }

    // Seed the first load without alerting -- those orders are new to this
    // component, not to the user.
    if (!this.seededInitialOrders) {
      _.forEach(orders, (order) => this.seenOrderIds.add(order._id));
      this.seededInitialOrders = true;
      return;
    }

    const arrivedOrders = _.filter(orders, (order) => !this.seenOrderIds.has(order._id));

    // Record before the early return so a suppressed alert can't re-fire later.
    _.forEach(arrivedOrders, (order) => this.seenOrderIds.add(order._id));

    if (!(this.shouldRespondToNewOrder && arrivedOrders.length > 0)) {
      return;
    }

    this.alertOrders = arrivedOrders;

    if (arrivedOrders.length > 1) {
      this.notifyOfMultipleOrders();
    } else if (arrivedOrders[0]._id !== this.lastDesktopNotificationId) {
      this.notifyOfSingleOrder();
    }
  });

  // Most recent arrivals -- drives the audible alert. Not @computed: a record of
  // what just arrived, not a derivation of current store state.
  @observable alertOrders = [];

  // Orders awaiting manual confirmation. Gates the confirm modal only, never
  // the alert -- an already-confirmed order must not be offered for re-confirm.
  @computed get openOrders() {
    return _.filter(this.props.ordersStore.active.orders, { status: 'open' });
  }

  @computed get multipleOrders() {
    return this.openOrders.length > 1;
  }

  notifyOfSingleOrder() {
    const newOrder = this.alertOrders[0];
    const { browserNotification, desktopNotification } = this.props.User.profile;
    this.lastDesktopNotificationId = newOrder._id;

    if (!desktopNotification) {
      return;
    }

    Push.create('You have a new dutchie order!', {
      body: 'Click to view the order',
      icon: '/icons/notification-icon-32x32.png',
      tag: `order:${newOrder._id}`,
      requireInteraction: true,
      onClick() {
        window.focus();
        this.close(); // This refers to the notification.
        if (browserNotification === false) {
          this.props.history.push(`/dispensaries/${newOrder.dispensaryId}/orders/edit/${newOrder._id}`);
        }
      },
    });
  }

  notifyOfMultipleOrders() {
    const { desktopNotification } = this.props.User.profile;

    if (!desktopNotification) {
      return;
    }

    Push.create('You have new dutchie orders!', {
      body: 'Confirm your orders to view more',
      icon: '/icons/notification-icon-32x32.png',
      tag: 'orders',
      requireInteraction: true,
      onClick() {
        window.focus();
        this.close(); // This refers to the notification.
      },
    });
  }

  render() {
    if (!this.shouldRespondToNewOrder || (_.isEmpty(this.openOrders) && _.isEmpty(this.alertOrders))) {
      return null;
    }

    const { browserNotification } = this.props.User.profile;

    if (browserNotification !== false) {
      return (
        <OrderNotification
          orders={this.openOrders}
          alertableOrders={this.alertOrders}
          multipleOrders={this.multipleOrders}
        />
      );
    }

    return null;
  }
}

export default withRouter(OpenOrdersNotification);
